import { Plus, Sparkles } from 'lucide-react';
import { ic, lc } from '../helpers';
import {
  OWNERSHIP_TYPES,
  VEHICLE_TYPES,
  RECREATIONAL_TYPES,
} from '../constants';

// ─────────────────────────────────────────
// STEP 4 — VEHICLES + RECREATIONAL UNITS
// ─────────────────────────────────────────
export default function Step4Vehicles({
  autos,
  aiExtractedAutos,
  personal,
  onUpdateAuto,
  onAddAuto,
  onRemoveAuto,
  recreationalUnits,
  onUpdateRecUnit,
  onAddRecUnit,
  onRemoveRecUnit,
}) {
  const fullName = [personal.firstName, personal.lastName].filter(Boolean).join(' ');

  return (
    <div className="space-y-4">
      {/* Vehicles */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between pb-1 border-b">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Vehicles</h2>
            {aiExtractedAutos.length > 0 && (
              <p className="text-xs text-purple-600 mt-0.5 flex items-center gap-1">
                <Sparkles size={10} /> Pre-filled by AI — please review
              </p>
            )}
          </div>
          <button
            onClick={onAddAuto}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
          >
            <Plus size={13} /> Add Vehicle
          </button>
        </div>
        {autos.map((auto, index) => (
          <div key={index} className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-700">Vehicle {index + 1}</span>
                {aiExtractedAutos.includes(index) && (
                  <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-lg">
                    <Sparkles size={10} /> AI filled
                  </span>
                )}
              </div>
              {autos.length > 1 && (
                <button onClick={() => onRemoveAuto(index)} className="text-xs text-red-500 hover:text-red-700">
                  Remove
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={lc}>Year</label>
                <input type="number" value={auto.year} onChange={e => onUpdateAuto(index, 'year', e.target.value)} placeholder="2020" className={ic} />
              </div>
              <div>
                <label className={lc}>Make</label>
                <input value={auto.make} onChange={e => onUpdateAuto(index, 'make', e.target.value)} placeholder="Toyota" className={ic} />
              </div>
              <div>
                <label className={lc}>Model</label>
                <input value={auto.model} onChange={e => onUpdateAuto(index, 'model', e.target.value)} placeholder="Camry" className={ic} />
              </div>
            </div>
            <div>
              <label className={lc}>VIN</label>
              <input
                value={auto.vin || ''}
                onChange={e => onUpdateAuto(index, 'vin', e.target.value)}
                placeholder="17-character VIN"
                maxLength={17}
                className={`${ic} font-mono tracking-wider uppercase`}
              />
            </div>
            <div>
              <label className={lc}>Trim / Spec</label>
              <input
                value={auto.trim || ''}
                onChange={e => onUpdateAuto(index, 'trim', e.target.value)}
                placeholder="e.g. EX-L · 2.5L I4 · 4-door · AWD"
                className={ic}
              />
            </div>
            <div>
              <label className={lc}>Vehicle Type</label>
              <select value={auto.vehicleType || 'Private Passenger'} onChange={e => onUpdateAuto(index, 'vehicleType', e.target.value)} className={ic}>
                {VEHICLE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Most cars/SUVs/trucks should stay as "Private Passenger". Change this only for motorcycles, antique/classic cars, golf carts, etc.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lc}>Name on Title</label>
                <input
                  value={auto.titleHolder || ''}
                  onChange={e => onUpdateAuto(index, 'titleHolder', e.target.value)}
                  placeholder={fullName || 'Full name'}
                  className={ic}
                />
              </div>
              <div>
                <label className={lc}>Primary Driver</label>
                <input
                  value={auto.primaryDriver || ''}
                  onChange={e => onUpdateAuto(index, 'primaryDriver', e.target.value)}
                  placeholder={fullName || 'Full name'}
                  className={ic}
                />
              </div>
            </div>
            <div>
              <label className={lc}>Ownership</label>
              <div className="flex gap-2">
                {OWNERSHIP_TYPES.map(type => (
                  <button
                    key={type}
                    onClick={() => onUpdateAuto(index, 'ownershipType', type)}
                    className={`flex-1 py-2 rounded-xl text-xs font-semibold border-2 transition ${
                      auto.ownershipType === type
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recreational Units */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between pb-1 border-b">
          <h2 className="text-base font-semibold text-slate-800">Recreational Units</h2>
          <button
            onClick={onAddRecUnit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
          >
            <Plus size={13} /> Add Unit
          </button>
        </div>
        {recreationalUnits.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">
            No recreational units added. Click "Add Unit" if you have any boats, motorcycles, ATVs, etc.
          </p>
        ) : (
          recreationalUnits.map((unit, index) => (
            <div key={index} className="border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">Unit {index + 1}</span>
                <button onClick={() => onRemoveRecUnit(index)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
              </div>
              <div>
                <label className={lc}>Type</label>
                <div className="flex flex-wrap gap-2">
                  {RECREATIONAL_TYPES.map(type => (
                    <button
                      key={type}
                      onClick={() => onUpdateRecUnit(index, 'unitType', type)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition ${
                        unit.unitType === type
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className={lc}>Year</label><input type="number" value={unit.year} onChange={e => onUpdateRecUnit(index, 'year', e.target.value)} placeholder="2020" className={ic} /></div>
                <div><label className={lc}>Make</label><input value={unit.make} onChange={e => onUpdateRecUnit(index, 'make', e.target.value)} placeholder="e.g. Sea-Doo" className={ic} /></div>
                <div><label className={lc}>Model</label><input value={unit.model} onChange={e => onUpdateRecUnit(index, 'model', e.target.value)} placeholder="e.g. GTX" className={ic} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={lc}>Name on Title</label><input value={unit.titleHolder} onChange={e => onUpdateRecUnit(index, 'titleHolder', e.target.value)} placeholder="Full name" className={ic} /></div>
                <div><label className={lc}>Primary Operator</label><input value={unit.primaryDriver} onChange={e => onUpdateRecUnit(index, 'primaryDriver', e.target.value)} placeholder="Full name" className={ic} /></div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}